/**
 * Search.js — Sitewide search results page.
 *
 * Accepts a text query and applies client-side filtering (practice area, format,
 * delivery mode, CPD category, date range) across all loaded Shopify products.
 * Renders results via ProductListing with pagination, and includes an optional
 * calendar view (react-big-calendar).
 *
 * Props:
 *  - ProductsData {object}                Raw Shopify product data keyed by handle.
 *  - Program_structure_images {Array}     Slug-to-image mapping.
 *  - Program_structure_color {Array}      Slug-to-colour mapping.
 *  - AddtoCart {Function}                 Adds a variant to the Shopify cart.
 *  - AddtoWishlist {Function}             Toggles a product in the wishlist.
 *  - getAllWishlist {Array}                Current wishlist slugs.
 *  - EngineURL {string}                   Base URL for the engine REST API.
 */

// ─── External libraries ───────────────────────────────────────────────────────
import React, { useState, useEffect, useRef, useCallback } from "react";
import Select from "../common/LcSelect";

import { ProductListing } from "./ProductListing";
import {
  LoaderIcon,
  SearchIconNew,
} from "../../icons";
import {
  Container,
  Row,
  Col,
  Form,
  FormControl,
  Button,
  Pagination,
  Modal,
} from "react-bootstrap";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router";
import queryString from "query-string";
import { useHistory } from "react-router-dom";
import axios from "axios";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import BlueBg from "../../images/blue-banner-img.png";
import DownloadIcon from "../../images/Download.svg";
import { Paginator } from "../Sections/Paginator";
import { Hint } from "react-autocomplete-hint";
import { CalendarToolbar } from "../Sections/CalendarToolbar";
import { LcViewToggle } from "../Sections/LcViewToggle";
import { sanitizeText } from "../../utils/text";
import { calendarRange, sanitizeDateInput } from "../../utils/date";

export const Search = (props) => {
  const location = useLocation();
  let history = useHistory();
  const queryParam = queryString.parse(location.search);
  const PathName = location.pathname;
  const productHandle =
    PathName.substring(PathName.lastIndexOf("/") + 1) !== ""
      ? PathName.substring(PathName.lastIndexOf("/") + 1)
      : "cpd-programs";

  const [seo_title, set_seo_title] = useState("");
  const [seo_description, set_seo_description] = useState("");
  const [Date_Filters_On__c, setDate_Filters_On__c] = useState("");
  const [ProgramType, setProgramType] = useState([]);
  const [PracticeArea, setPracticeArea] = useState([]);
  const [CPDCategories, setCPDCategories] = useState([]);
  const [DeliverMode, setDeliveryMode] = useState([]);
  const [formatType, setformatType] = useState([]);
  const [Topics, setTopics] = useState([]);
  const localizer = momentLocalizer(moment);
  const [LayoutView, setLayoutView] = useState(
    queryParam["view"] !== undefined ? "Calendar view" : "List view"
  );
  const [calendarView, setCalendarView] = useState("month");
  const [expandAll] = useState(true);

  /***
   * Remove special charachter
   */
  const RemoveSpecialChar = (StringText) => {
    if (StringText !== null && StringText !== undefined) {
      return StringText.toLowerCase()
        .split("&")
        .join("and")
        .split(",")
        .join(" ")
        .split(" ")
        .join("-");
    }
  };

  let events = [];
  let FilterProductsID = [];


  useEffect(() => {

    set_seo_title(() => {
      return "Search results";
    });

    set_seo_description(() => {
      let mydata = "";   

      props.ProductPageJson.forEach((menu) => {
        if (
          menu.Name.split(" ").join("-").toLowerCase() === productHandle &&
          menu.SEO_Content !== null
        ) {
          mydata = menu.SEO_Content.replace(/<[^>]*>?/gm, "");
        }
      });
      return mydata;
    });

    setDate_Filters_On__c(() => {
      return true;
    });

    /***
     * Topic list
     */
    setTopics(() => {
      return [
        "Administrative Law",
        "Civil Litigation",
        "Commercial and Corporate Practice",
        "Employment and Industrial Relations",
        //  "Ethics and Professional Responsibility",
        "Lawyer’s Skills including Advocacy",
        "Problem Solving",
        "Property Law",
        "Risk Management",
        "Trust and Office Accounting",
        "Work Management Business Skills (including Risk Management)",
      ];
    });

    /***
     * Types of format
     */
    setformatType(() => {
      return ["Pre-Recorded Webcasts", "10 CPD Point Packages", "Books"];
    });

    /***
     * Delivery Mode
     */
    setDeliveryMode(() => {
      return ["Face to Face", "Live Stream"];
    });

    /**
     * CPD Categories
     */
    setCPDCategories(() => {
      return [
        "Ethics & Professional Responsibility",
        "Professional Skills",
        "Practice Management & Business Skills",
        "Substantive Law",
      ];
    });

    /***
     * Practice Area
     */
    setPracticeArea([
      "Building & Construction",
      "Business & Contracts",
      "Consumer",
      "Corporate & In-House Counsel",
      "Costs",
      "Criminal",
      "Employment & Workplace Relations",
      "Ethics",
      "Family",
      "Finance, Tax, Superannuation",
      "Government, Privacy & FOI",
      "Insolvency & Restructuring",
      "Intellectual Property",
      "Legal Practice Fundamentals",
      "Litigation & Advocacy",
      "Migration",
      "Personal Injury & Medico-Legal",
      "Professional Skills & Practice Management",
      "Property, Leases & Planning",
      "Sports Law",
      "Wills, Trusts & Succession Planning",
    ]);

    /***
     * Program Type
     */
    setProgramType(() => {
      return [
        "Live Streamed One Hour Seminars",
        "Live Streamed Conferences and Intensives",
        "Live Streamed Practical Workshops",
        "Onsite Programs",
        "Pre-Recorded Webcasts",
      ];
    });
  },[set_seo_title,productHandle,props.ProductPageJson]);

  const eventStyleGetter = (event, start, end, isSelected) => {
    const customClass = event.customClass;
    return {
      className: customClass,
    };
  };

  /****
   * Update calendar event list
   * Phase 2
   */

  // ─── Calendar toolbar — shared component, proxy-button prefix = "rbc" ─────────
  const CustomToolbar = (tbProps) => <CalendarToolbar {...tbProps} togglePrefix="rbc" />;

  // ─── Event title renderer (matches All Programs) ────────────────────────────
  const EventComponent = ({ event }) => <>{event.title}</>;

  // ─── Mobile inline event panel (Figma 118:77104) ────────────────────────────
  const [mobileSelectedDate, setMobileSelectedDate] = useState(null);
  const [mobileSelectedEvents, setMobileSelectedEvents] = useState([]);
  const [panelTop, setPanelTop] = useState(null);
  const calendarWrapperRef = useRef(null);
  const selectedRowRef = useRef(null);
  const panelRef = useRef(null);

  const isMobileViewport = () =>
    typeof window !== 'undefined' && window.innerWidth <= 767;

  const sameDay = (a, b) =>
    a && b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const getDayEvents = (cellDate) =>
    events.filter((ev) => sameDay(new Date(ev.start), cellDate));

  /**
   * Opens (or collapses) the mobile inline detail panel for a day cell.
   * Tapping the already-open day collapses it; a day with no events is a no-op.
   * Desktop never calls this — the calendar keeps its normal drill-down.
   */
  const toggleMobileDayPanel = (cellDate, rowEl) => {
    const dayEvents = getDayEvents(cellDate);
    if (!dayEvents.length) return;
    if (sameDay(mobileSelectedDate, cellDate)) {
      setMobileSelectedDate(null);
      setMobileSelectedEvents([]);
      return;
    }
    selectedRowRef.current = rowEl;
    setMobileSelectedDate(cellDate);
    setMobileSelectedEvents(dayEvents);
  };

  // Open a single gap for the panel by pushing the row right after the selected
  // week down by the panel height (a margin there shifts every following row too,
  // so the rest of the month keeps normal spacing).
  useEffect(() => {
    if (calendarWrapperRef.current) {
      calendarWrapperRef.current
        .querySelectorAll('.rbc-month-row')
        .forEach((r) => {
          r.style.removeProperty('margin-top');
          r.style.removeProperty('margin-bottom');
        });
    }
    if (mobileSelectedDate && calendarWrapperRef.current && selectedRowRef.current) {
      const wrapperRect = calendarWrapperRef.current.getBoundingClientRect();
      const rowRect = selectedRowRef.current.getBoundingClientRect();
      setPanelTop(rowRect.bottom - wrapperRect.top);
      requestAnimationFrame(() => {
        if (panelRef.current && selectedRowRef.current && calendarWrapperRef.current) {
          const panelHeight = panelRef.current.offsetHeight;
          const allRows = Array.from(calendarWrapperRef.current.querySelectorAll('.rbc-month-row'));
          const nextRow = allRows[allRows.indexOf(selectedRowRef.current) + 1];
          // Open a gap the size of the panel. Inline !important beats the
          // stylesheet's `.rbc-month-row + .rbc-month-row { margin-top: 0 !important }`,
          // and the mobile month-view is height:auto so the calendar box grows to
          // contain the panel. For the LAST week there's no row below to push, so
          // grow the box by adding the gap below the selected row instead.
          if (nextRow) {
            nextRow.style.setProperty('margin-top', panelHeight + 'px', 'important');
          } else {
            selectedRowRef.current.style.setProperty('margin-bottom', panelHeight + 'px', 'important');
          }
        }
      });
    } else {
      setPanelTop(null);
    }
  }, [mobileSelectedDate]);

  // ─── Day cell wrapper — adds lc-cal-cell-wrapper + event dot ────────────────
  const DateCellWrapperComponent = ({ children, value }) => {
    const cellDate = new Date(value);
    const dayEvents = getDayEvents(cellDate);
    const hasEvent = dayEvents.length > 0;
    return (
      <div
        className={`lc-cal-cell-wrapper${hasEvent ? ' lc-cal-has-event' : ''}`}
        onClick={(e) => {
          // Mobile-only inline panel — never alter the desktop calendar layout.
          if (!isMobileViewport()) return;
          if (hasEvent) {
            toggleMobileDayPanel(cellDate, e.currentTarget.closest('.rbc-month-row'));
          }
        }}
      >
        {children}
        {hasEvent && (
          /* One dot per event — stacked horizontally (mobile view). */
          <span className="lc-cal-event-dots">
            {dayEvents.map((_, i) => (
              <span key={i} className="lc-cal-event-dot" />
            ))}
          </span>
        )}
      </div>
    );
  };

  /**
   * The month-cell date number. react-big-calendar's default click drills down
   * into the Day view — on mobile that navigated away from the month grid. Here
   * we intercept the tap on mobile to open the inline collapse panel instead;
   * desktop falls through to the default drill-down.
   */
  const DateHeaderComponent = ({ label, date, onDrillDown }) => (
    <button
      type="button"
      className="rbc-button-link"
      onClick={(e) => {
        if (isMobileViewport()) {
          e.preventDefault();
          toggleMobileDayPanel(date, e.currentTarget.closest('.rbc-month-row'));
          return;
        }
        onDrillDown(e);
      }}
    >
      {label}
    </button>
  );

  // Week / Day column header — shows the day name with the date number beneath
  // it (e.g. "SUN" over "1"), so the weekly view carries date information.
  // `label` is the day name (from dayFormat 'ddd'); `date` gives the number.
  // Month view is unaffected — it keeps its own dateHeader.
  const WeekDayHeaderComponent = ({ label, date }) => (
    <span className="rbc-weekday-header">
      <span className="rbc-weekday-name">{label}</span>
      <span className="rbc-weekday-date">{date.getDate()}</span>
    </span>
  );

  const UpdateCalendarEvent = (myEvents) => {
    var event_list = [];
    var products_id = [];
    for (var s = 0; s < myEvents.length; s++) {
      let ppp = Object.values(myEvents[s]);
      for (var edge = 0; edge < (ppp[1]?.metafields || []).length; edge++) {
        var metadata = (ppp[1]?.metafields || [])[edge];
        if (metadata.key === "all_data") {
          var prodcutJson = JSON.parse(metadata.value);
          if (
            prodcutJson.start_date !== undefined &&
            prodcutJson.end_date !== undefined &&
            prodcutJson.start_date !== null &&
            prodcutJson.end_date !== null
          ) {
            event_list.push({
              ...calendarRange(prodcutJson.start_date_time, prodcutJson.end_date_time),
              url: props.storeURL + "product/" + ppp[1].handle,
              title: sanitizeText(ppp[1].title),
              customClass: program_date_is_gone_or_not(prodcutJson.start_date)
                ? "pgone"
                : "",
            });
          }
        }
      }
      products_id.push(ppp[1].handle);
    }
    events = event_list;
    FilterProductsID = products_id;
  };

  /***
   * Event click
   * Phase 2
   */
  const EventClick = (event) => {
    window.location.href = event.url;
  };

  /**
   * Fetch product data
   */
  const [isLoading, setLoading] = useState(false);

  const resultsRef = useRef(null);

  const ResetFilter_Data = useCallback(() => {
    
    setProgram([]);
    setPractise([]);
    setTopic([]);
    setCPD([]);
    setSearchText_("");
    setSearchText("");
    setSearchTextTextField("");
    setStartDate(null);
    setEndDate(null);
    setselectedFormat([]);
    setPromotion(undefined);
    setPromotion(false);
    setDelivery([]);
    setScrollTrigger(0);
    history.push(PathName);
  }, [history, PathName]);

  const fetchData = useCallback(async (tags) => {

    setLoading(true);

    try {
      const tag_url = tags !== "" ? "?tag=" + tags : "";
      const api_URL = props.EngineURL + "shopify-pings/getdata.php" + tag_url;
      const response = await axios(api_URL);

      /***
       * Assign data to State
       */
      if (response.status === 200) {
        var data = response.data;
        if (data.length !== 0) {
          var result = Object.keys(data).map((key) => [Number(key), data[key]]);
          setUserData_({ products: result });
          settotalProduct(result);
        } else {
          setUserData_({ products: [] });
          settotalProduct(0);
        }
        setLoading(false);
      }
    } catch (error) {
    }
    setLoading(false);
  },[props.EngineURL]);

  /***
   * WHen user click on Menu
   */
  useEffect(() => {
    fetchData("");
  }, [fetchData]);

  useEffect(() => {
    if (
      props.lastURL !== undefined &&
      props.lastURL !== location.search &&
      location.search === ""
    ) {
      ResetFilter_Data();
    }
    props.setlastURL(location.search);
  }, [location.search, ResetFilter_Data, props]);

  /***
   * Get value from query URL
   */
  const GAurl =
    queryParam["gclid"] !== undefined ? "?gclid=" + queryParam["gclid"] : "";

  const [selectedProgram, setProgram] = useState(() => {
    return queryParam["program-type"] !== undefined
      ? queryParam["program-type"].split(",")
      : [];
  });
  const [SortBy, setSortBy] = useState(() => {
    return queryParam["sort-by"] !== undefined
      ? queryParam["sort-by"]
      : "date-az";
  });
  const [selectedPractise, setPractise] = useState(() => {
    return queryParam["practice-area"] !== undefined
      ? queryParam["practice-area"].split(",")
      : [];
  });
  const [selectedCPD, setCPD] = useState(() => {
    return queryParam["cpd-categories"] !== undefined
      ? queryParam["cpd-categories"].split(",")
      : [];
  });

  const [selectedTopic, setTopic] = useState(() => {
    return queryParam["topic"] !== undefined
      ? queryParam["topic"].split(",")
      : [];
  });

  const [SearchText, setSearchText] = useState(() => {
    return queryParam["keyword"] !== undefined ? queryParam["keyword"] : "";
  });

  const [SearchTextTextField, setSearchTextTextField] = useState(() => {
    return queryParam["keyword"] !== undefined ? queryParam["keyword"] : "";
  });

  const [SearchText_, setSearchText_] = useState(() => {
    return queryParam["keyword"] !== undefined ? queryParam["keyword"] : "";
  });

  const [scrollTrigger, setScrollTrigger] = useState(
    queryParam["keyword"] !== undefined ? 1 : 0
  );

  useEffect(() => {
    if (scrollTrigger === 0) return;
    const doScroll = () => {
      const el = document.querySelector('.results_container');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    if (!isLoading) {
      setTimeout(doScroll, 150);
    }
  }, [scrollTrigger, isLoading]);

  const [startDateFocus, setStartateFocus] = useState(false);
  const [endDateFocus, setEndDateFocus] = useState(false);
  const [startDate, setStartDate] = useState(() => {
    return queryParam["start-date"] !== undefined
      ? queryParam["start-date"]
      : undefined;
  });
  const [endDate, setEndDate] = useState(() => {
    return queryParam["end-date"] !== undefined
      ? queryParam["end-date"]
      : undefined;
  });
  const [selectedFormat, setselectedFormat] = useState(() => {
    return queryParam["format"] !== undefined
      ? queryParam["format"].split(",")
      : [];
  });
  const [isPromotion, setPromotion] = useState(() => {
    return queryParam["ispromotion"] !== undefined
      ? queryParam["ispromotion"]
      : false;
  });
  const [selectedDelivery, setDelivery] = useState(() => {
    return queryParam["stream-mode"] !== undefined
      ? queryParam["stream-mode"].split(",")
      : [];
  });

  const [selectedState] = useState(() => {
    return queryParam["state"] !== undefined
      ? queryParam["state"].split(",")
      : [];
  });

  const [userData_, setUserData_] = useState(undefined);

  

  /***
   * Pagignation Object
   */
  const [totalProduct, settotalProduct] = useState(0);
  const [pageSize] = useState(16);
  const [currentPage, setcurrentPage] = useState(() => {
    return queryParam["page"] !== undefined ? queryParam["page"] : 1;
  });

  const [PaginationRange, setPaginationRange] = useState(0);

  /***
   * Generate URL from user selection
   */
  function GenerateDomURLforFilter() {
    if (
      currentPage !== 1 ||
      selectedProgram.length ||
      selectedPractise.length ||
      selectedCPD.length ||
      isPromotion !== false ||
      SearchText_ !== "" ||
      selectedDelivery.length !== 0 ||
      (startDate !== null &&
        endDate !== null &&
        startDate !== undefined &&
        endDate !== undefined) ||
      selectedTopic.length ||
      selectedFormat.length ||
      selectedState.length ||
      SortBy !== "date-az"
    ) {      
      var MakeURL = "?";
      if (
        selectedProgram !== undefined &&
        selectedProgram !== null &&
        selectedProgram.length
      ) {
        MakeURL +=
          "&program-type=" +
          selectedProgram.map((v) => RemoveSpecialChar(v)).join(",");
      }
      if (selectedState !== undefined && selectedState.length) {
        MakeURL +=
          "&state=" + selectedState.map((v) => RemoveSpecialChar(v)).join(",");
      }
      if (selectedPractise !== undefined && selectedPractise.length) {
        MakeURL +=
          "&practice-area=" +
          selectedPractise.map((v) => RemoveSpecialChar(v)).join(",");
      }
      if (selectedCPD !== undefined && selectedCPD.length) {
        MakeURL +=
          "&cpd-categories=" +
          selectedCPD.map((v) => RemoveSpecialChar(v)).join(",");
      }
      if (selectedDelivery !== undefined && selectedDelivery.length) {
        MakeURL +=
          "&stream-mode=" +
          selectedDelivery.map((v) => RemoveSpecialChar(v)).join(",");
      }
      if (selectedTopic !== undefined && selectedTopic.length) {
        MakeURL +=
          "&topic=" + selectedTopic.map((v) => RemoveSpecialChar(v)).join(",");
      }
      if (selectedFormat !== undefined && selectedFormat.length) {
        MakeURL +=
          "&format=" +
          selectedFormat.map((v) => RemoveSpecialChar(v)).join(",");
      }
      if (isPromotion !== false) {
        MakeURL += "&ispromotion=true";
      }

      if (SearchText_ !== "") {
        MakeURL += "&keyword=" + SearchText_;
      }

      if (
        startDate !== null &&
        endDate !== null &&
        startDate !== undefined &&
        endDate !== undefined
      ) {
        MakeURL += "&start-date=" + startDate + "&end-date=" + endDate;
      }

      if (currentPage !== 1) {
        MakeURL += "&page=" + currentPage;
      }

      if (SortBy !== "date-az") {
        MakeURL += "&sort-by=" + SortBy;
      }
      if (queryParam["gclid"] !== undefined) {
        MakeURL += "&gclid=" + queryParam["gclid"];
      }
      if (queryParam["utm_medium"] !== undefined) {
        MakeURL += "&utm_medium=" + queryParam["utm_medium"];
      }
      if (queryParam["utm_campaign"] !== undefined) {
        MakeURL += "&utm_campaign=" + queryParam["utm_campaign"];
      }
      if (queryParam["utm_content"] !== undefined) {
        MakeURL += "&utm_content=" + queryParam["utm_content"];
      }
      if (queryParam["view"] !== undefined) {
        MakeURL += "&view=" + queryParam["view"];
      }
      if (queryParam["utm_source"] !== undefined) {
        MakeURL += "&utm_source=" + queryParam["utm_source"];
      }
      if (queryParam["utm_term"] !== undefined) {
        MakeURL += "&utm_term=" + queryParam["utm_term"];
      }
      if (window.location.hash) {
        MakeURL += "" + window.location.hash;
      }

      return MakeURL;
    }
    return undefined;
  }

  // Initializing didMount as false
  const [didMount, setDidMount] = useState(false);

  // Setting didMount to true upon mounting
  useEffect(() => {
    setDidMount(true);
  }, []);

  /**
   * change current page when filter updated
   */
  useEffect(() => {
    if (didMount) {
      setcurrentPage(1);

      const newurl = GenerateDomURLforFilter();
      if (newurl !== undefined) {
        history.push(PathName + "" + newurl);
      }
    }
  }, [
    selectedProgram,
    selectedPractise,
    selectedCPD,
    isPromotion,
    selectedDelivery,
    startDate,
    endDate,
    selectedTopic,
    selectedFormat,
    SearchText_
  ]);

  useEffect(() => {
    const newurl = GenerateDomURLforFilter();
    if (newurl !== undefined) {
      history.push(PathName + "" + newurl);
    }
  }, [currentPage]);

  function getPrevPage() {
    return currentPage === 1 ? 1 : currentPage - 1;
  }

  function getNextPage() {
    return currentPage < PaginationRange ? currentPage + 1 : PaginationRange;
  }

  /**
   * WHnen prodcut has been filter recalculate total product
   */
  function handler(val) {
    settotalProduct(val);
    setPaginationRange(Math.ceil(val / pageSize));
  }

  /***
   * Compare array value
   */
  function intersect_arrays(a, b) {
    var sorted_a = a.concat().sort();
    var sorted_b = b.concat().sort();
    var common = [];
    var a_i = 0;
    var b_i = 0;

    while (a_i < a.length && b_i < b.length) {
      if (sorted_a[a_i] === sorted_b[b_i]) {
        common.push(sorted_a[a_i]);
        a_i++;
        b_i++;
      } else if (sorted_a[a_i] < sorted_b[b_i]) {
        a_i++;
      } else {
        b_i++;
      }
    }
    return common;
  }

  function dateInPast(firstDate, secondDate) {
    if (firstDate.setHours(0, 0, 0, 0) <= secondDate.setHours(0, 0, 0, 0)) {
      return true;
    }

    return false;
  }

  /***
   * Check date between
   */
  function compare_date(dateFrom, dateTo, dateCheck) {
    var d1 = dateFrom.split("-");
    var d2 = dateTo.split("-");
    var c = dateCheck.split("-");

    var from = new Date(d1[0], parseInt(d1[1]) - 1, d1[2]); // -1 because months are from 0 to 11
    var to = new Date(d2[0], parseInt(d2[1]) - 1, d2[2]);
    var check = new Date(c[0], parseInt(c[1]) - 1, c[2]);

    return from <= check && check <= to;
  }

  /***
   * Detect enter key
   */
  const HitEnter = (event) => {
    event.preventDefault();
    setSearchText_(SearchText);
    if (SearchText !== "") setScrollTrigger(t => t + 1);
  };

  const program_date_is_gone_or_not = (program_start_date_time) => {
    if (program_start_date_time === null) {
      return false;
    }
    program_start_date_time = new Date(program_start_date_time);
    let program_time_modify = program_start_date_time.setHours(
      program_start_date_time.getHours()
    );
    let current_time = new Date().getTime();
    if (parseFloat(program_time_modify) - parseFloat(current_time) > 0) {
      return false;
    } else {
      return true;
    }
  };

  const is_ten_minite_less = (program_start_date_time) => {
    if (
      program_start_date_time === undefined ||
      program_start_date_time === null ||
      program_start_date_time === ""
    ) {
      return false;
    }

    program_start_date_time = new Date(program_start_date_time);
    let program_time_modify = program_start_date_time.setHours(
      program_start_date_time.getHours()
    );
    let current_time_10 = new Date(new Date().getTime() + 1000 * 600);
    let current_time = new Date().getTime();

    var from = current_time; // -1 because months are from 0 to 11
    var to = current_time_10;
    var check = program_time_modify;

    return from <= check && check <= to;
  };

  /***
   * Filter value from main array
   */
  function mysearch(data) {
    const filterRespones = data.filter(function (pppp) {
      let ppp = Object.values(pppp);
      var program_type = false;
      var practise_area = false;
      var cpd_categories = false;
      var check_promotion = false;
      var deliver_mode = false;
      var start_date = false;
      var end_date = false;
      var search_text = false;
      var defaaultt = false;
      var topic = false;
      var hidefromlist = false;
      var datapassed = true;
      var format_ = false;
      var state_allowed = true;
      // alert(selectedTopic);
      //alert(selectedTopic);
      var CurrentDate = new Date();

      if (
        selectedFormat !== "" ||
        isPromotion === true ||
        isPromotion === "true" ||
        (startDate !== null && endDate !== null) ||
        SearchText_ !== "" ||
        selectedProgram !== undefined ||
        selectedPractise !== undefined ||
        selectedCPD !== undefined ||
        selectedDelivery.length ||
        selectedTopic !== undefined
      ) {
        
        (ppp[1]?.metafields || []).forEach(function (metadata) {
          if (metadata.key === "all_data") {
            var prodcutJson = JSON.parse(metadata.value);

            /****
             * Old date
             */

            var is_program_gone = program_date_is_gone_or_not(
              prodcutJson.start_date_time
            );
            if (prodcutJson.start_date_time !== null && is_program_gone) {
              datapassed = false;
            }

            /* var past = new Date(prodcutJson.end_date);

              if (prodcutJson.end_date != undefined && dateInPast(past, CurrentDate)) {
                  datapassed = false;
              }*/

            /***
             * Hide from list
             */

            if (prodcutJson.Hide_from_list !== true) {
              hidefromlist = true;
            }

            if (selectedState !== undefined && selectedState.length) {
              if (prodcutJson.Program_Jurisdiction__c !== null && prodcutJson.Program_Jurisdiction__c !== undefined) {
                var lower_state = prodcutJson.Program_Jurisdiction__c.split(";").map((v) => RemoveSpecialChar(v));
                var selectedState_user = selectedState.map((v) => RemoveSpecialChar(v));
                selectedState_user.push('national');
                if (intersect_arrays(lower_state, selectedState_user).length === 0 || (prodcutJson.RecordType !== undefined && prodcutJson.RecordType == 'PTC Course')) {
                  state_allowed = false;
                }
              } else {
                state_allowed = false;
              }
            }

            /**
             * Program type
             */

            if (selectedProgram !== undefined && selectedProgram.length) {
              if (
                prodcutJson.Program_Types__c !== null &&
                prodcutJson.Program_Types__c !== undefined
              ) {
                let lower_program = prodcutJson.Program_Types__c.map((v) =>
                  RemoveSpecialChar(v)
                );
                let selectedProgram_user = selectedProgram.map((v) =>
                  RemoveSpecialChar(v)
                );
                if (
                  intersect_arrays(lower_program, selectedProgram_user)
                    .length !== 0
                ) {
                  program_type = true;
                }
              }
            } else {
              program_type = true;
            }

            /****
             * Practice area
             */
            if (selectedPractise !== undefined && selectedPractise.length) {
              if (prodcutJson.Practising_Areas__c !== null) {
                let lower_program = prodcutJson.Practising_Areas__c.split(
                  ";"
                ).map((v) => RemoveSpecialChar(v));
                var selectedPractise_user = selectedPractise.map((v) =>
                  RemoveSpecialChar(v)
                );
                if (
                  intersect_arrays(lower_program, selectedPractise_user)
                    .length !== 0
                ) {
                  practise_area = true;
                }
              }
            } else {
              practise_area = true;
            }

            /****
             * CPD Categories
             */
            if (selectedCPD !== undefined && selectedCPD.length) {
              if (
                prodcutJson.Ethics_Professional_Responsibility__c !== 0 &&
                selectedCPD.includes("ethics-and-professional-responsibility")
              ) {
                cpd_categories = true;
              }
              if (
                prodcutJson.Practice_Management_Business_Skills__c !== 0 &&
                selectedCPD.includes("practice-management-and-business-skills")
              ) {
                cpd_categories = true;
              }
              if (
                prodcutJson.Substantive_Law__c !== 0 &&
                selectedCPD.includes("substantive-law")
              ) {
                cpd_categories = true;
              }
              if (
                prodcutJson.Professional_Skills__c !== 0 &&
                selectedCPD.includes("professional-skills")
              ) {
                cpd_categories = true;
              }
            } else {
              cpd_categories = true;
            }

            /**
             * Product title
             */
            if (SearchText_ !== "") {
              let myReg = new RegExp(
                SearchText_.toLowerCase()
                  .split(" ")
                  .join("-")
                  .replace(/[^a-zA-Z0-9]/g, "") + ".*"
              );

              if (
                ppp[1].handle
                  .toLowerCase()
                  .split(" ")
                  .join("-")
                  .replace(/[^a-zA-Z0-9]/g, "")
                  .match(myReg) !== null ||
                ppp[1].title
                  .toLowerCase()
                  .split(" ")
                  .join("-")
                  .replace(/[^a-zA-Z0-9]/g, "")
                  .match(myReg) ||
                ppp[1].description
                  .toLowerCase()
                  .split(" ")
                  .join("-")
                  .replace(/[^a-zA-Z0-9]/g, "")
                  .match(myReg)
              ) {
                search_text = true;
              } else {
                if (!search_text) {                  
                  prodcutJson.presentor.forEach((presentor) => {
                    presentor.Contact_info.forEach((Contact_info) => {
                      if (
                        Contact_info.Name.toLowerCase()
                          .split(" ")
                          .join("-")
                          .replace(/[^a-zA-Z0-9]/g, "")
                          .match(myReg) !== null
                      ) {
                        search_text = true;
                      }
                    });
                  });
                }
                
                if (!search_text) {
                  if (
                    prodcutJson.Program_Types__c !== null &&
                    prodcutJson.Program_Types__c !== undefined
                  ) {
                    var lower_program = prodcutJson.Program_Types__c.map((v) =>
                      RemoveSpecialChar(v)
                    );
                    let selectedProgram_user = RemoveSpecialChar(SearchText_);
                    if (lower_program.includes(selectedProgram_user)) {
                      search_text = true;
                    }
                  }
                }
                if (!search_text) {
                  if (prodcutJson.Practising_Areas__c !== null) {
                    let lower_program = prodcutJson.Practising_Areas__c.split(
                      ";"
                    ).map((v) => RemoveSpecialChar(v));
                    let selectedPractise_user = [
                      RemoveSpecialChar(SearchText_),
                    ];
                    if (
                      intersect_arrays(lower_program, selectedPractise_user)
                        .length !== 0
                    ) {
                      search_text = true;
                    }
                  }
                }

                if (!search_text) {
                  if (
                    prodcutJson.Ethics_Professional_Responsibility__c !== 0 &&
                    RemoveSpecialChar(SearchText_) ===
                    "ethics-and-professional-responsibility"
                  ) {
                    search_text = true;
                  }
                  if (
                    prodcutJson.Practice_Management_Business_Skills__c !== 0 &&
                    RemoveSpecialChar(SearchText_) ===
                    "practice-management-and-business-skills"
                  ) {
                    search_text = true;
                  }
                  if (
                    prodcutJson.Substantive_Law__c !== 0 &&
                    RemoveSpecialChar(SearchText_) === "substantive-law"
                  ) {
                    search_text = true;
                  }
                  if (
                    prodcutJson.Professional_Skills__c !== 0 &&
                    RemoveSpecialChar(SearchText_) === "professional-skills"
                  ) {
                    search_text = true;
                  }
                }
                if (!search_text) {
                  if (prodcutJson.slt_topic !== null) {
                    let lower_program = prodcutJson.slt_topic
                      .split(";")
                      .map((v) => RemoveSpecialChar(v));
                    let selectedPractise_user = [
                      RemoveSpecialChar(SearchText_),
                    ];
                    if (
                      intersect_arrays(lower_program, selectedPractise_user)
                        .length !== 0
                    ) {
                      search_text = true;
                    }
                  }
                }

                if (!search_text) {
                  if (prodcutJson.Format__c !== null) {
                    let lower_program = prodcutJson.Format__c.split(";").map(
                      (v) => RemoveSpecialChar(v)
                    );
                    let selectedPractise_user = [
                      RemoveSpecialChar(SearchText_),
                    ];
                    if (
                      intersect_arrays(lower_program, selectedPractise_user)
                        .length !== 0
                    ) {
                      search_text = true;
                    }
                  }
                }

                if (!search_text) {
                  if (prodcutJson.delivery_mode != null) {
                    let lower_program = prodcutJson.delivery_mode.map((v) =>
                      RemoveSpecialChar(v)
                    );
                    var lower_program_user = [RemoveSpecialChar(SearchText_)];
                    if (
                      intersect_arrays(lower_program, lower_program_user)
                        .length !== 0
                    ) {
                      search_text = true;
                    }
                  }
                }
              }
            } else {
              search_text = true;
            }

            /***
             * isPromotion
             */
            if (isPromotion === true || isPromotion === "true") {
              if (prodcutJson.isPromotion === true) {
                check_promotion = true;
              }
            } else {
              check_promotion = true;
            }

            /**
             * Selected topic
             */
            if (selectedTopic !== undefined && selectedTopic.length) {
              if (prodcutJson.slt_topic !== null) {
                let lower_program = prodcutJson.slt_topic
                  .split(";")
                  .map((v) => RemoveSpecialChar(v));
                let selectedPractise_user = selectedTopic.map((v) =>
                  RemoveSpecialChar(v)
                );
                if (
                  intersect_arrays(lower_program, selectedPractise_user)
                    .length !== 0
                ) {
                  topic = true;
                }
              }
            } else {
              topic = true;
            }

            /**
             * Selected format
             */
            if (selectedFormat !== undefined && selectedFormat.length) {
              if (prodcutJson.Format__c !== null) {
                let lower_program = prodcutJson.Format__c.split(";").map((v) =>
                  RemoveSpecialChar(v)
                );
                let selectedPractise_user = selectedFormat.map((v) =>
                  RemoveSpecialChar(v)
                );
                if (
                  intersect_arrays(lower_program, selectedPractise_user)
                    .length !== 0
                ) {
                  format_ = true;
                }
              }
            } else {
              format_ = true;
            }

            /**
             * Deliver Mode
             */
            if (selectedDelivery.length !== 0) {
              if (prodcutJson.delivery_mode != null) {
                let lower_program = prodcutJson.delivery_mode.map((v) =>
                  RemoveSpecialChar(v)
                );
                let lower_program_user = selectedDelivery.map((v) =>
                  RemoveSpecialChar(v)
                );
                if (
                  intersect_arrays(lower_program, lower_program_user).length !==
                  0
                ) {
                  deliver_mode = true;
                }
              }
            } else {
              deliver_mode = true;
            }

            /***
             * Start Date
             */
            if (
              startDate !== null &&
              endDate !== null &&
              startDate !== undefined &&
              endDate !== undefined
            ) {
              var is_date_avaialble = false;

              if (
                prodcutJson.start_date === undefined ||
                prodcutJson.end_date === undefined ||
                prodcutJson.start_date === null ||
                prodcutJson.end_date === null
              ) {
                is_date_avaialble = true;
              }

              if (
                prodcutJson.start_date !== undefined &&
                prodcutJson.start_date !== null &&
                compare_date(startDate, endDate, prodcutJson.start_date)
              ) {
                start_date = true;
                end_date = true;
              }
              if (
                prodcutJson.end_date !== undefined &&
                prodcutJson.end_date !== null &&
                compare_date(startDate, endDate, prodcutJson.end_date)
              ) {
                start_date = true;
                end_date = true;
              }

              if (is_date_avaialble) {
                // start_date = true;
                // end_date = true;
              }
            } else {
              start_date = true;
              end_date = true;
            }



            if (
              state_allowed &&
              format_ &&
              datapassed &&
              hidefromlist &&
              check_promotion &&
              program_type &&
              practise_area &&
              cpd_categories &&
              deliver_mode &&
              search_text &&
              start_date &&
              end_date &&
              topic
            ) {
              defaaultt = true;
            }
          }
        });
      } else {
        /****
         * Hide from list
         */        
        (ppp[1]?.node?.metafields?.edges || []).forEach(function (metadata) {
          if (metadata.key === "all_data") {
            var prodcutJson = JSON.parse(metadata.value);
            if (prodcutJson.Hide_from_list !== true) {
              hidefromlist = true;
            }

            var past = new Date(prodcutJson.end_date);

            if (
              prodcutJson.end_date !== undefined &&
              dateInPast(past, CurrentDate)
            ) {
              datapassed = true;
            }
          }
        });

        if (hidefromlist) {
          defaaultt = true;
        } else {
          defaaultt = false;
        }

        if (datapassed) {
          defaaultt = true;
        }
      }

      return defaaultt;
    });

    /***
     * Sort — respects SortBy dropdown for all options.
     * When a date-range filter is active (Date_Filters_On__c), date sort takes
     * precedence unless the user has explicitly chosen a non-date sort option.
     */

    // Helper: extract start_date_time from metafields
    const getStartDate = (item) => {
      let d = null;
      (item[1]?.metafields || []).forEach(function (metadata) {
        if (metadata.key === "all_data") {
          d = JSON.parse(metadata.value).start_date_time;
        }
      });
      return d;
    };

    // Date sort comparator (shared by date-az, date-za, and Date_Filters_On__c fallback)
    const dateComparator = (aa, bb, ascending) => {
      let a = Object.values(aa);
      let b = Object.values(bb);
      var date1 = getStartDate(a);
      var date2 = getStartDate(b);
      // Undated / on-demand ("to download") items always sort to the end, in
      // both directions. Check BOTH sides first so the comparator stays
      // consistent (returning 0 when both are undated) — an inconsistent
      // comparator scrambles the whole date sort.
      const aEnd = !date1 || (a[1].tags || []).includes("online-intensive-(to-download)");
      const bEnd = !date2 || (b[1].tags || []).includes("online-intensive-(to-download)");
      if (aEnd && bEnd) return 0;
      if (aEnd) return 1;
      if (bEnd) return -1;
      return ascending
        ? new Date(date1) - new Date(date2)
        : new Date(date2) - new Date(date1);
    };

    // When a date-range filter is active and user hasn't picked a non-date sort,
    // force date-ascending so results respect the selected date window
    const forceDateSort =
      Date_Filters_On__c && SortBy !== "price-az" && SortBy !== "price-za" &&
      SortBy !== "title-az" && SortBy !== "title-za" && SortBy !== "onsale" &&
      SortBy !== "date-za";

    if (SortBy === "date-az" || forceDateSort) {
      filterRespones.sort((aa, bb) => dateComparator(aa, bb, true));
    } else if (SortBy === "date-za") {
      filterRespones.sort((aa, bb) => dateComparator(aa, bb, false));
    } else if (SortBy === "title-az") {
      filterRespones.sort((a, b) =>
        (a[1].title || "").localeCompare(b[1].title || "")
      );
    } else if (SortBy === "title-za") {
      filterRespones.sort((a, b) =>
        (b[1].title || "").localeCompare(a[1].title || "")
      );
    } else if (SortBy === "price-az") {
      // Shopify returns amount as a string — parseFloat ensures numeric comparison
      filterRespones.sort((a, b) =>
        parseFloat(a[1].priceRange?.minVariantPrice?.amount || 0) -
        parseFloat(b[1].priceRange?.minVariantPrice?.amount || 0)
      );
    } else if (SortBy === "price-za") {
      filterRespones.sort((a, b) =>
        parseFloat(b[1].priceRange?.minVariantPrice?.amount || 0) -
        parseFloat(a[1].priceRange?.minVariantPrice?.amount || 0)
      );
    } else if (SortBy === "onsale") {
      filterRespones.sort(function (aa, bb) {
        let a = Object.values(aa);
        let b = Object.values(bb);
        var price1 = null;
        var price2 = null;
        (a[1]?.metafields || []).forEach(function (metadata) {
          if (metadata.key === "all_data") {
            // Compare_Price is a numeric price string, not a date
            price1 = JSON.parse(metadata.value).Compare_Price;
          }
        });
        (b[1]?.metafields || []).forEach(function (metadata) {
          if (metadata.key === "all_data") {
            price2 = JSON.parse(metadata.value).Compare_Price;
          }
        });
        // Items with a Compare_Price (on sale) sort first
        return parseFloat(price2 || 0) - parseFloat(price1 || 0);
      });
    } else {
      // Default fallback: date ascending (matches initial SortBy value "date-az")
      filterRespones.sort((aa, bb) => dateComparator(aa, bb, true));
    }

    /*****
     * Update calendar event
     */
    UpdateCalendarEvent(filterRespones);
    return filterRespones;
  }

  /***
   * Remove array value
   */
  const removeItemOnce = (arr, value) => {
    var index = arr.indexOf(value);
    if (index > -1) {
      arr.splice(index, 1);
    }
    return arr;
  };

  /***
   * Convert array to selectio option
   */
  const getSelectOption = (optionList, DefaultLabel) => {
    let option_list = [];
    if (DefaultLabel === "program_array") {
      ProgramType.forEach((value) => {
        if (
          optionList.length &&
          optionList.includes(RemoveSpecialChar(value))
        ) {
          option_list.push({
            label: value,
            value: RemoveSpecialChar(value),
          });
        }
      });
    } else if (DefaultLabel === "topic_array") {     
      Topics.forEach((value) => {
        if (
          optionList.length &&
          optionList.includes(RemoveSpecialChar(value))
        ) {
          option_list.push({
            label: value,
            value: RemoveSpecialChar(value),
          });
        }
      });
    } else if (DefaultLabel === "format_array") {      
      formatType.forEach((value) => {
        if (
          optionList.length &&
          optionList.includes(RemoveSpecialChar(value))
        ) {
          option_list.push({
            label: value,
            value: RemoveSpecialChar(value),
          });
        }
      });
    } else if (DefaultLabel === "practice_array") {      
      PracticeArea.forEach((value) => {
        if (
          optionList.length &&
          optionList.includes(RemoveSpecialChar(value))
        ) {
          option_list.push({
            label: value,
            value: RemoveSpecialChar(value),
          });
        }
      });
    } else if (DefaultLabel === "cpd_array") {      
      CPDCategories.forEach((value) => {
        if (
          optionList.length &&
          optionList.includes(RemoveSpecialChar(value))
        ) {
          option_list.push({
            label: value,
            value: RemoveSpecialChar(value),
          });
        }
      });
    } else if (DefaultLabel === "remove space") {
      DeliverMode.forEach((value) => {
        if (optionList.includes(RemoveSpecialChar(value))) {
          option_list.push({
            label: value,
            value: RemoveSpecialChar(value),
          });
        }
      });
    } else {     
      optionList.forEach((value) => {
        option_list.push({
          label: value,
          value: RemoveSpecialChar(value),
        });
      });
    }

    return option_list;
  };

  const ManageDeliveryMode = (dmode) => {
    var temp = [];
    dmode.forEach((opt) => {
      temp.push(opt.value);
    });
    setDelivery(temp);
  };

  const ManagePrograme = (dmode) => {
    var temp = [];
    dmode.forEach((opt) => {
      temp.push(opt.value);
    });
    setProgram(temp);
  };

  const ManageFormat = (dmode) => {
    var temp = [];
    dmode.forEach((opt) => {
      temp.push(opt.value);
    });
    setselectedFormat(temp);
  };

  const ManagePractice = (dmode) => {
    var temp = [];    
    dmode.forEach((opt) => {
      temp.push(opt.value);
    });
    setPractise(temp);
  };

  const ManageCPD = (dmode) => {
    var temp = [];
    dmode.forEach((opt) => {
      temp.push(opt.value);
    });
    setCPD(temp);
  };
  const ManageTopic = (dmode) => {
    var temp = [];
    dmode.forEach((opt) => {
      temp.push(opt.value);
    });
    setTopic(temp);
  };

  const ManagePromotioan = (val) => {
    setPromotion(val === null ? false : true);
  };

  /****
   * Manage Popular tag click event
   */
  const ClickPopularTag = (TagValue) => {
    if (Does_Tag_isProgramType(TagValue).length > 0) {
      selectedProgram.includes(RemoveSpecialChar(TagValue))
        ? setProgram([
          ...removeItemOnce(selectedProgram, RemoveSpecialChar(TagValue)),
        ])
        : setProgram((oldArray) => [...oldArray, RemoveSpecialChar(TagValue)]);
      return true;
    } else if (Does_Tag_isFormatType(TagValue).length > 0) {
      selectedFormat.includes(RemoveSpecialChar(TagValue))
        ? setselectedFormat([
          ...removeItemOnce(selectedFormat, RemoveSpecialChar(TagValue)),
        ])
        : setselectedFormat((oldArray) => [
          ...oldArray,
          RemoveSpecialChar(TagValue),
        ]);
      return true;
    } else if (Does_Tag_isPracticeArea(TagValue).length > 0) {
      selectedPractise.includes(RemoveSpecialChar(TagValue))
        ? setPractise([
          ...removeItemOnce(selectedPractise, RemoveSpecialChar(TagValue)),
        ])
        : setPractise((oldArray) => [...oldArray, RemoveSpecialChar(TagValue)]);
      return true;
    } else if (Does_Tag_isTopics(TagValue).length > 0) {
      selectedTopic.includes(RemoveSpecialChar(TagValue))
        ? setTopic([
          ...removeItemOnce(selectedTopic, RemoveSpecialChar(TagValue)),
        ])
        : setTopic((oldArray) => [...oldArray, RemoveSpecialChar(TagValue)]);
      return true;
    } else if (Does_Tag_isCPDCategories(TagValue).length > 0) {
      selectedCPD.includes(RemoveSpecialChar(TagValue))
        ? setCPD([...removeItemOnce(selectedCPD, RemoveSpecialChar(TagValue))])
        : setCPD((oldArray) => [...oldArray, RemoveSpecialChar(TagValue)]);
      return true;
    } else if (Does_Tag_isDeliverMode(TagValue).length > 0) {
      selectedDelivery.includes(RemoveSpecialChar(TagValue))
        ? setDelivery([
          ...removeItemOnce(selectedDelivery, RemoveSpecialChar(TagValue)),
        ])
        : setDelivery((oldArray) => [...oldArray, RemoveSpecialChar(TagValue)]);
      return true;
    }
  };

  /***
   * Is filter Tag checked
   */

  const isTagCHecked = (TagValue) => {
    if (
      Does_Tag_isProgramType(TagValue).length > 0 &&
      selectedProgram !== undefined
    ) {
      return selectedProgram.includes(RemoveSpecialChar(TagValue));
    } else if (Does_Tag_isFormatType(TagValue).length > 0) {
      return selectedFormat.includes(RemoveSpecialChar(TagValue));
    } else if (
      Does_Tag_isPracticeArea(TagValue).length > 0 &&
      selectedPractise !== undefined
    ) {
      return selectedPractise.includes(RemoveSpecialChar(TagValue));
    } else if (Does_Tag_isTopics(TagValue).length > 0) {
      return selectedTopic.includes(RemoveSpecialChar(TagValue));
    } else if (
      Does_Tag_isCPDCategories(TagValue).length > 0 &&
      selectedCPD !== undefined
    ) {
      return selectedCPD.includes(RemoveSpecialChar(TagValue));
    } else if (Does_Tag_isDeliverMode(TagValue).length > 0) {
      return selectedDelivery.includes(RemoveSpecialChar(TagValue));
    }
  };

  /****
   * All filter list
   */
  const AllFilterList = (RequestTag) => {
    var ArraysTemp = [
      [ProgramType],
      [formatType],
      [PracticeArea],
      [Topics],
      [CPDCategories],
      [DeliverMode],
    ];
    return flatten(ArraysTemp).includes(RequestTag) && isTagCHecked(RequestTag);
  };

  const flatten = (arr) => {
    return arr.reduce(function (flat, toFlatten) {
      return flat.concat(
        Array.isArray(toFlatten) ? flatten(toFlatten) : toFlatten
      );
    }, []);
  };

  /***
   *
   */
  const Does_Tag_isProgramType = (ProgramValue) => {
    return ProgramType.filter((val) => {
      return RemoveSpecialChar(val) === RemoveSpecialChar(ProgramValue);
    });
  };

  const Does_Tag_isFormatType = (FormatValue) => {
    return formatType.filter((val) => {
      return RemoveSpecialChar(val) === RemoveSpecialChar(FormatValue);
    });
  };

  const Does_Tag_isPracticeArea = (PracticeValue) => {
    return PracticeArea.filter((val) => {
      return RemoveSpecialChar(val) === RemoveSpecialChar(PracticeValue);
    });
  };

  const Does_Tag_isTopics = (TopicValue) => {
    return Topics.filter((val) => {
      return RemoveSpecialChar(val) === RemoveSpecialChar(TopicValue);
    });
  };

  const Does_Tag_isCPDCategories = (CategoriesValue) => {
    return CPDCategories.filter((val) => {
      return RemoveSpecialChar(val) === RemoveSpecialChar(CategoriesValue);
    });
  };

  const Does_Tag_isDeliverMode = (DeliverModeValue) => {
    return DeliverMode.filter((val) => {
      return RemoveSpecialChar(val) === RemoveSpecialChar(DeliverModeValue);
    });
  };

  /***
   * Auto complete list
   */

  const AutoComplteList = () => {
    var ArraysTemp = [
      [ProgramType],
      [formatType],
      [PracticeArea],
      [Topics],
      [CPDCategories],
      [DeliverMode],
    ];
    var ReturnArray = [];
    flatten(ArraysTemp).forEach((val) => {
      ReturnArray.push(val);
    });
    return ReturnArray;
  };

  /****
   *
   */
  const AutoCompltehandleOnSelect = (item) => {
    let TagValue = item;
    setSearchText(TagValue);
    setSearchTextTextField(TagValue);
  };

  /****
   * Sort by option
   */
  const [SortbyOption] = useState([
    {
      label: "Price Low to High",
      value: "price-az",
    },
    {
      label: "Price High to low",
      value: "price-za",
    },
    {
      label: "On Sale",
      value: "onsale",
    },
    {
      label: "Title (A-Z)",
      value: "title-az",
    },
    {
      label: "Title (Z-A)",
      value: "title-za",
    },
    {
      label: "Date (Earliest-Latest)",
      value: "date-az",
    },
    {
      label: "Date (Latest-Earliest)",
      value: "date-za",
    },
  ]);

  const getDefaultSortby = () => {
    return SortbyOption.filter((val) => {
      return val.value === SortBy;
    });
  };

  const [DownloadButtonText, setDownloadButtonText] = useState("Download PDF");
  const [userEmail, setUserEmail] = useState("");
  const [EmailModal, setEmailModal] = useState(false);
  const [EmailModalMessage, setEmailModalMessage] = useState(undefined);
  const [userEmail_ER, set_userEmail_ER] = useState(false);
  const validateEmail = (email) => {
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  };
  useEffect(() => {
    if (
      props.useLoggedIn !== undefined &&
      props.useLoggedIn === true &&
      props.UserData !== null &&
      props.UserData !== undefined
    ) {
      setUserEmail(props.UserData.email);
    }
  }, [props.UserData,props.useLoggedIn]);

  const MakePDFready = () => {
    set_userEmail_ER(false);

    // Guard against double submits (rapid/repeat clicks): the button text
    // itself is the in-flight flag, so bail out if a request is already
    // running or already succeeded, instead of firing another API call.
    if (
      DownloadButtonText === "Validating..." ||
      DownloadButtonText === "PDF has been sent to your email."
    ) {
      return;
    }

    const trimmedEmail = (userEmail || "").trim();

    if (trimmedEmail !== "") {
      if (!validateEmail(trimmedEmail)) {
        set_userEmail_ER(true);
        setEmailModal(true);
        return;
      }
      setDownloadButtonText("Validating...");
      axios
        .post(props.AdminPanelURL + "api/pdf/addtosqs", {
          id: FilterProductsID,
          email: trimmedEmail,
        },{
        headers: {
          "Content-Type": "application/json",
          'X-API-KEY': props.API_KEY   // ← Add this
        },
      })
        .then(function (response) {
          if (response.status === 200) {
            setDownloadButtonText("PDF has been sent to your email.");
            setEmailModalMessage("PDF has been sent to your email.");
            // downloadPDF(response.data.pdf, "product-list.pdf");
          } else {
            setDownloadButtonText("Download PDF");
            setEmailModalMessage(response.data?.message || "Something went wrong. Please try again.");
          }
        }) .catch(function (error) {
          // Previously unhandled: a network/validation/5xx error left the
          // button stuck on "Validating..." forever with no feedback.
          setDownloadButtonText("Download PDF");
          const status = error?.response?.status;
          if (status === 429) {
            setEmailModalMessage(
              "Too many requests — please wait a moment and try again."
            );
          } else if (status === 422) {
            setEmailModalMessage("Please check your email address and try again.");
          } else {
            setEmailModalMessage("Something went wrong. Please try again.");
          }
        });
    } else {
      if (EmailModal && (userEmail === null || !validateEmail(userEmail))) {
        set_userEmail_ER(true);
      }

      setEmailModal(true);
    }
  }; 

  /****
   * Fillter 3 dot toogle
   */
  const [dotToogle, setdotToogle] = useState(false);

  if (isLoading) {
    return (
      <>
        <div className="pre_loader l1">
          <LoaderIcon />
        </div>
      </>
    );
  }

  if (userData_ !== undefined && userData_ !== "") {
    return (
      <>
        <Helmet>
          <title>
            {seo_title} {seo_title !== "" ? "|" : ""} Leo Cussen Centre for Law
          </title>
          <link
            rel="canonical"
            href={"https://store.leocussen.edu.au/all-courses"}
          />
          <meta name="description" content={seo_description} />
        </Helmet>

        <div className="all_program_page">
          <div className="search_wrapper">
            <Container>
              <div
                className="blue_banner_bar"
                style={{ backgroundImage: "url(" + BlueBg + ")" }}
              ></div>
              <h1 className="search_wrapper_title">ALL PROGRAMS</h1>
              <div className="search_inner">
                <Form onSubmit={HitEnter}>
                  <div className={`filter_search_block${SearchTextTextField !== "" && !(scrollTrigger > 0 && totalProduct === 0 && !isLoading) ? " filter_search_block--active" : ""}${SearchTextTextField !== "" && scrollTrigger > 0 && totalProduct === 0 && !isLoading ? " filter_search_block--error" : ""}`}>
                    <div className="sc-eCYdqJ">
                      <div className="wrapper">
                        <div className="sc-hKMtZM jVgFww d-none d-md-block">
                          <Hint options={AutoComplteList()} allowTabFill={true}>
                            <input
                              className="search_filed"
                              placeholder="Search for a program title, program description or presenter name"
                              value={SearchTextTextField}
                              onChange={(e) => {
                                  const val = e.target.value;
                                  AutoCompltehandleOnSelect(val);
                                  if (val === '') {
                                      setSearchText('');
                                      setSearchText_('');
                                      setScrollTrigger(0);
                                  }
                              }}
                            />
                          </Hint>
                        </div>
                        <div className="sc-hKMtZM jVgFww d-md-none">
                          <Hint options={AutoComplteList()} allowTabFill={true}>
                            <input
                              className="search_filed"
                              placeholder="Search for a program title, program description or presenter name"
                              value={SearchTextTextField}
                              onChange={(e) => {
                                  const val = e.target.value;
                                  AutoCompltehandleOnSelect(val);
                                  if (val === '') {
                                      setSearchText('');
                                      setSearchText_('');
                                      setScrollTrigger(0);
                                  }
                              }}
                            />
                          </Hint>
                        </div>
                      </div>
                    </div>
                    <div className={`search-btns-reveal${SearchTextTextField ? ' search-btns-reveal--active' : ''}`}>
                      <button
                        type="button"
                        className="search-clear-btn"
                        onClick={() => { setSearchText(""); setSearchTextTextField(""); setSearchText_(""); setScrollTrigger(0); }}
                        aria-label="Clear search"
                        tabIndex={SearchTextTextField ? 0 : -1}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 4L4 12" stroke="#231F20" strokeWidth="1.3" strokeLinecap="round"/>
                          <path d="M4 4L12 12" stroke="#231F20" strokeWidth="1.3" strokeLinecap="round"/>
                        </svg>
                      </button>
                      <Button type="submit" className="search-submit-btn" tabIndex={SearchTextTextField ? 0 : -1}>Search</Button>
                    </div>
                    <Button type="submit" variant="search">
                      <SearchIconNew />
                    </Button>
                  </div>
                  {SearchTextTextField !== "" && scrollTrigger > 0 && totalProduct === 0 && !isLoading && (
                    <p className="search-no-results-msg">No records found</p>
                  )}
                  <div className={'fillter_wrapper filter_open'}>
                    <div
                      className="filter_list_wrapper"
                      style={dotToogle ? { height: "auto" } : {}}
                    >
                      <label className="filter_title">Filter by:</label>
                      {props.PopularTag.map((TagName, key) => {
                        return (
                          <div className="filter_list" key={key + "ss"}>
                            <div className="form-check">
                              <div
                                className={
                                  AllFilterList(TagName)
                                    ? "form-check-label active"
                                    : "form-check-label"
                                }
                                id={RemoveSpecialChar(TagName)}
                                onClick={() => ClickPopularTag(TagName)}
                              >
                                {TagName}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {!dotToogle ? (
                        <div
                          className="filter_list_more"
                          onClick={() => setdotToogle(true)}
                        >
                          ...
                        </div>
                      ) : null}
                    </div>
                    <ul className="filter_dropdown_list list-unstyled">
                      <li>
                        <span className='filter_field_label'>Program Type</span>
                        <Select
                          aria-label="select"
                          key={selectedProgram.toString()}
                          options={getSelectOption(ProgramType, "Program Type")}
                          placeholder="Please select"
                          isClearable={true}
                          defaultValue={getSelectOption(
                            selectedProgram,
                            "program_array"
                          )}
                          isMulti={true}
                          onChange={ManagePrograme}
                        />
                      </li>
                      <li>
                        <span className='filter_field_label'>Format Type</span>
                        <Select
                          key={selectedFormat}
                          aria-label="select"
                          options={getSelectOption(formatType, "Format Type")}
                          defaultValue={getSelectOption(
                            selectedFormat,
                            "format_array"
                          )}
                          placeholder="Please select"
                          isMulti={true}
                          isClearable={true}
                          onChange={ManageFormat}
                        />
                      </li>
                      <li>
                        <span className='filter_field_label'>Practice Areas</span>
                        <Select
                          key={selectedPractise}
                          aria-label="select"
                          options={getSelectOption(
                            PracticeArea,
                            "Practice Areas"
                          )}
                          defaultValue={getSelectOption(
                            selectedPractise,
                            "practice_array"
                          )}
                          placeholder="Please select"
                          isMulti={true}
                          isClearable={true}
                          onChange={ManagePractice}
                        />
                      </li>
                      <li>
                        <span className='filter_field_label'>Topic</span>
                        <Select
                          key={selectedTopic}
                          aria-label="select"
                          options={getSelectOption(Topics, "Topic")}
                          defaultValue={getSelectOption(
                            selectedTopic,
                            "topic_array"
                          )}
                          placeholder="Please select"
                          isMulti={true}
                          isClearable={true}
                          onChange={ManageTopic}
                        />
                      </li>
                      <li>
                        <span className='filter_field_label'>CPD Categories</span>
                        <Select
                          key={selectedCPD}
                          aria-label="select"
                          options={getSelectOption(
                            CPDCategories,
                            "CPD Categories"
                          )}
                          defaultValue={getSelectOption(
                            selectedCPD,
                            "cpd_array"
                          )}
                          placeholder="Please select"
                          isMulti={true}
                          isClearable={true}
                          onChange={ManageCPD}
                        />
                      </li>
                      <li>
                        <span className='filter_field_label'>From Date</span>
                        <FormControl
                          type={startDateFocus ? "date" : "text"}
                          onFocus={() => setStartateFocus(true)}
                          placeholder="From date"
                          className="date_filed"
                          min="1900-01-01"
                          max="9999-12-31"
                          value={startDate != null ? startDate : ""}
                          onChange={(e) =>
                            setStartDate(sanitizeDateInput(e.target.value, startDate))
                          }
                          aria-label="From Date"
                        />
                      </li>
                      <li>
                        <span className='filter_field_label'>To Date</span>
                        <FormControl
                          type={endDateFocus ? "date" : "text"}
                          onFocus={() => setEndDateFocus(true)}
                          placeholder="To date"
                          className="date_filed"
                          min={startDate || "1900-01-01"}
                          max="9999-12-31"
                          aria-label="To Date"
                          value={endDate != null ? endDate : ""}
                          onChange={(e) =>
                            setEndDate(sanitizeDateInput(e.target.value, endDate))
                          }
                        />
                      </li>
                      <li>
                        <span className='filter_field_label'>Delivery Mode</span>
                        <Select
                          id="delivery_mode_select"
                          key={selectedDelivery.toString()}
                          aria-label="select"
                          options={getSelectOption(
                            DeliverMode,
                            "Delivery Mode"
                          )}
                          placeholder="Please select"
                          defaultValue={getSelectOption(
                            selectedDelivery,
                            "remove space"
                          )}
                          isMulti={true}
                          isClearable={true}
                          onChange={ManageDeliveryMode}
                        />
                      </li>
                      <li>
                        <span className='filter_field_label'>Promotions</span>
                        <Select
                          aria-label="select"
                          options={[{ label: "Yes", value: "true" }]}
                          placeholder="Please select"
                          isClearable={true}
                          onChange={ManagePromotioan}
                        />
                      </li>

                      <li>
                        <div
                          className="reset_btn btn btn-outline-primary"
                          onClick={() => ResetFilter_Data()}
                        >
                          Reset
                        </div>
                      </li>
                    </ul>
                  </div>
                </Form>
              </div>
            </Container>
          </div>

          <Container className="results_container" ref={resultsRef}>
            <Row>
              <Col md={12} xs={12}>
                <div className="cpd_programs_content">
                  <div className="cpd_head">
                    {SearchText_ !== "" && <h1 className="lc-reveal-pending lc-reveal">Search results</h1>}
                    {totalProduct > 0 ? (
                      <button className="downloadpdf btn btn-link p-0" onClick={() => MakePDFready()} disabled={DownloadButtonText !== "Download PDF"}>
                        {DownloadButtonText}
                        {DownloadButtonText === "Download PDF" && (
                          <img src={DownloadIcon} alt="" className="downloadpdf-icon" />
                        )}
                      </button>
                    ) : null}
                    <p class="fs-6 text-danger">{EmailModalMessage === 'PDF has been sent to your email.' ? '' : EmailModalMessage}</p>
                  </div>

                  <div className="programs_description">
                    <p>{seo_description}</p>
                  </div>

                  <div className="results_sort_order_block d-flex flex-column flex-md-row align-items-sm-center justify-content-between">
                    <div className="results_block d-flex align-items-center">
                      <h4>Results</h4>
                      <Form.Check
                        inline
                        label="List view"
                        id="switch_view_list"
                        name="switch_view"
                        value="List view"
                        defaultChecked={LayoutView === "List view"}
                        onChange={(e) => setLayoutView(e.target.value)}
                        type="radio"
                      />
                      <Form.Check
                        inline
                        label="Calendar view"
                        id="switch_view_calendar"
                        name="switch_view"
                        value="Calendar view"
                        defaultChecked={LayoutView === "Calendar view"}
                        onChange={(e) => setLayoutView(e.target.value)}
                        type="radio"
                      />

                    </div>
                    <div className="sort_order_block d-flex flex-column flex-sm-row align-items-sm-center">
                      {LayoutView === "List view" ? (
                        <>
                          <span>Sort by:</span>
                          <Select
                            aria-label="Sort by"
                            options={SortbyOption}
                            placeholder="Sort by"
                            defaultValue={getDefaultSortby}
                            onChange={(val) => setSortBy(val.value)}
                          />
                        </>
                      ) : (
                        <LcViewToggle
                          id="lc-ext-toggle"
                          view={calendarView}
                          items={[
                            { label: "Month", id: "rbc-toggle-month", view: "month" },
                            { label: "Week", id: "rbc-toggle-week", view: "week" },
                            { label: "Day", id: "rbc-toggle-day", view: "day" },
                            { label: "Agenda", id: "rbc-toggle-agenda", view: "agenda" },
                          ]}
                        />
                      )}
                    </div>
                  </div>
                  <div className={`cpd_results_list${LayoutView !== "List view" ? " d-none" : ""}`}>
                    <div className="cpd_programs">
                      <Row xs={1} className="g-4">
                        <ProductListing
                          key="0"
                          GAurl={GAurl}
                          pageSize={pageSize}
                          totalProduct={totalProduct}
                          currentPage={currentPage}
                          statehandler={handler}
                          AddtoCart={props.AddtoCart}
                          data={mysearch(userData_.products)}
                          Program_structure_images={
                            props.Program_structure_images
                          }
                          is_ten_minite_less={is_ten_minite_less}
                          program_date_is_gone_or_not={
                            program_date_is_gone_or_not
                          }
                          Program_structure_color={
                            props.Program_structure_color
                          }
                          getAllWishlist={props.getAllWishlist}
                          AddtoWishlist={props.AddtoWishlist}
                          expandAll={expandAll}
                          EngineURL={props.EngineURL}
                        />
                      </Row>
                    </div>
                    {PaginationRange > 1 ? (
                      <Pagination className="justify-content-center flex-wrap">
                        <Pagination.Item className="page_link_first" onClick={(e) => { e.preventDefault(); setcurrentPage(1); }}>First</Pagination.Item>
                        {currentPage !== getPrevPage() ? (
                          <Pagination.Prev
                            onClick={(e) => { e.preventDefault(); setcurrentPage(getPrevPage()); }}
                            className="page_link_prev"
                          />
                        ) : (
                          ""
                        )}
                        {PaginationRange <= 6 ? (
                          [...Array(PaginationRange)].map((_, index) => {
                            let page_current_render = index + 1;
                            return (
                              <Pagination.Item
                                key={"page" + index}
                                className={
                                  page_current_render === currentPage
                                    ? "active"
                                    : null
                                }
                                onClick={(e) => { e.preventDefault(); setcurrentPage(page_current_render); }}
                              >
                                {page_current_render}
                              </Pagination.Item>
                            );
                          })
                        ) : (
                          <Paginator
                            key="sdf"
                            totalPages={PaginationRange}
                            currentPage={currentPage}
                            setcurrentPage={setcurrentPage}
                          />
                        )}

                        {currentPage !== getNextPage() ? (
                          <Pagination.Next
                            data-next={getNextPage()}
                            onClick={(e) => { e.preventDefault(); setcurrentPage(getNextPage()); }}
                            className="page_link_next"
                          />
                        ) : (
                          ""
                        )}
                        <Pagination.Item className="page_link_last" onClick={(e) => { e.preventDefault(); setcurrentPage(PaginationRange); }}>Last</Pagination.Item>
                      </Pagination>
                    ) : (
                      ""
                    )}
                  </div>
                </div>
                <div ref={calendarWrapperRef} style={{ position: 'relative' }}>
                  <Calendar
                    localizer={localizer}
                    onSelectEvent={(event) => EventClick(event)}
                    startAccessor="start"
                    endAccessor="end"
                    style={{ height: 800 }}
                    events={events}
                    className={LayoutView === "List view" ? "d-none" : ""}
                    popup
                    onView={setCalendarView}
                    eventPropGetter={eventStyleGetter}
                    step={60}
                    timeslots={1}
                    min={new Date(0, 0, 0, 7, 0, 0)}
                    max={new Date(0, 0, 0, 20, 0, 0)}
                    formats={{
                      dayFormat: 'ddd',
                      dayRangeHeaderFormat: ({ start, end }, culture, localizer) =>
                        localizer.format(start, 'MMMM YYYY', culture),
                      agendaDateFormat: 'ddd D MMM YYYY',
                      agendaTimeFormat: 'h:mm A',
                      agendaHeaderFormat: ({ start, end }, culture, localizer) =>
                        localizer.format(start, 'MMMM D', culture) + ' – ' + localizer.format(end, 'MMMM D', culture),
                    }}
                    components={{
                      toolbar: CustomToolbar,
                      event: EventComponent,
                      dateCellWrapper: DateCellWrapperComponent,
                      month: { dateHeader: DateHeaderComponent },
                      week: { header: WeekDayHeaderComponent },
                      day: { header: WeekDayHeaderComponent },
                    }}
                  />
                  {mobileSelectedDate && mobileSelectedEvents.length > 0 && panelTop !== null && (
                    <div
                      ref={panelRef}
                      className={`lc-cal-mobile-panel${LayoutView === "List view" ? " d-none" : ""}`}
                      style={{ position: 'absolute', top: panelTop, left: 0, right: 0, zIndex: 10 }}
                    >
                      <div className="lc-cal-mobile-panel-inner">
                        <div className="lc-cal-mobile-panel-date">
                          <span className="lc-cal-date-bullet" />
                          {mobileSelectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                        </div>
                        {mobileSelectedEvents.map((ev, i) => (
                          <div key={i} className="lc-cal-mobile-event-card" onClick={() => EventClick(ev)}>
                            <div className="lc-cal-event-title">{ev.title}</div>
                            <span className="lc-cal-view-course">View course</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </Container>
        </div>
        <>
          <Modal
            className="login_modal"
            show={EmailModal}
            onHide={() => setEmailModal(false)}
            backdrop="static"
            keyboard={false}
          >
            <Modal.Header closeButton className="border-0"></Modal.Header>
            <Modal.Body>
              {EmailModalMessage === undefined ? (
                <>
                  <p>
                    We will send you an email once PDF is ready
                  </p>
                  <Form.Group as={Col} md="12" xs="12" className="form-group">
                    <Form.Control
                      type="email"
                      className={userEmail_ER ? "form-error" : ""}
                      id="pdfemail"
                      placeholder="Your email address*"
                      value={userEmail}
                      onChange={(e) => { setUserEmail(e.target.value); if (userEmail_ER) set_userEmail_ER(false); }}
                    />
                    {userEmail_ER && <span className="field-error-msg">Please enter a valid email address</span>}
                  </Form.Group>
                  <div className="text-center d-flex justify-content-between">
                    <Button
                      variant="primary"
                      onClick={() => MakePDFready()}
                      type="button"
                      className="rounded-2 btn-lg border-2 ms-0 m-2 w-50"
                      disabled={DownloadButtonText !== "Download PDF"}
                    >
                      {DownloadButtonText === "Download PDF"
                        ? "Submit"
                        : "Submiting..."}
                    </Button>
                  </div>
                </>
              ) : (
                <p className="text-center">{EmailModalMessage}</p>
              )}
            </Modal.Body>
          </Modal>
        </>
      </>
    );
  }
  return (
    <>
      <div className="pre_loader l2">
        <LoaderIcon />
      </div>
    </>
  );
};
